import { z } from 'zod';

import { BillType } from '../enums/billType';
import { messagingVolumeWithCostSchema, openAiUsageRowSchema } from './admin';
import { dateRangeQuerySchema } from './common';

// ============================================
// Daily Engagement Analytics Schemas
// ============================================

export const dailyEngagementQuerySchema = dateRangeQuerySchema;

export const dailyEngagementRowSchema = z.object({
  date: z.string(),
  uniqueDailyActiveUsers: z.number(),
  newActiveUsers: z.number(),
  totalMessages: z.number(),
  campaignMessages: z.number(),
});

export const dailyEngagementResponseSchema = z.array(dailyEngagementRowSchema);

// Types
export type DailyEngagementQuery = z.infer<typeof dailyEngagementQuerySchema>;
export type DailyEngagementRow = z.infer<typeof dailyEngagementRowSchema>;
export type DailyEngagementResponse = z.infer<
  typeof dailyEngagementResponseSchema
>;

// ============================================
// Cost Analytics Schemas
// ============================================

export const costAnalyticsQuerySchema = z.object({
  startDate: z.string(),
  endDate: z.string(),
});

export const categoryCostBreakdownSchema = z.object({
  messages: z.number(),
  cost: z.number(),
});

/*
 * A bill line plus the delivery counts for the same (date, country, category).
 *
 * Volume/unitPrice/cost come from the billing pipeline; the status counts are
 * always read from ClickHouse, because Meta's pricing_analytics has no delivery
 * dimension. The two therefore come from DIFFERENT ledgers and need not agree:
 * the counts are undefined when our DB has no record of the group at all (a
 * business that sends through its own Cloud API — render 'N/A', never 0, which
 * would wrongly assert nothing was delivered), and a row can carry counts with
 * volume 0 when ClickHouse saw traffic the bill did not charge for (e.g. a day
 * where every message failed).
 *
 * Counts are cumulative funnel values, so sentCount >= deliveredCount:
 * sentCount counts messages that reached 'sent' or beyond, deliveredCount those
 * that reached 'delivered' or beyond (read/clicked/responded roll up).
 */
export const costAnalyticsVolumeRowSchema =
  messagingVolumeWithCostSchema.extend({
    sentCount: z.number().optional(),
    deliveredCount: z.number().optional(),
    failedCount: z.number().optional(),
  });

/*
 * Messaging volume part of cost analytics. Volume is sourced through the SAME
 * pipeline that generates bills (fetchMessagingVolumeByType), so totals match
 * the invoice; `volumeRows` are the bill's per-country/category lines (there is
 * deliberately NO template dimension — Meta pricing_analytics doesn't have one,
 * and per-template delivery stats already live in template analytics).
 */
export const costAnalyticsMessagingSchema = z.object({
  volumeRows: z.array(costAnalyticsVolumeRowSchema),
  totalMessagingCost: z.number(),
  categoryBreakdown: z.object({
    MARKETING: categoryCostBreakdownSchema,
    UTILITY: categoryCostBreakdownSchema,
    AUTHENTICATION: categoryCostBreakdownSchema,
  }),
  /*
   * How volume was actually fetched: 'standard' = ClickHouse standard scan;
   * 'conservative' = Meta pricing_analytics with ClickHouse conservative
   * fallback (KARIX orgs also resolve as conservative in cost analytics —
   * the Karix CSV only exists at cycle end).
   */
  volumeSource: z.nativeEnum(BillType).optional(),
});

// AI (token) usage part of cost analytics
export const costAnalyticsAiUsageSchema = z.object({
  rows: z.array(openAiUsageRowSchema),
  totalTokens: z.number(),
  totalCostUsd: z.number(),
  totalCostInr: z.number(),
  usdToInrRate: z.number(),
});

// Combined response: messaging volume + AI usage + grand total (INR)
export const costAnalyticsResponseSchema = costAnalyticsMessagingSchema.extend({
  aiUsage: costAnalyticsAiUsageSchema,
  grandTotalCost: z.number(),
});

// Types
export type CostAnalyticsQuery = z.infer<typeof costAnalyticsQuerySchema>;
export type CostAnalyticsVolumeRow = z.infer<
  typeof costAnalyticsVolumeRowSchema
>;
export type CostAnalyticsMessaging = z.infer<
  typeof costAnalyticsMessagingSchema
>;
export type CostAnalyticsAiUsage = z.infer<typeof costAnalyticsAiUsageSchema>;
export type CostAnalyticsResponse = z.infer<typeof costAnalyticsResponseSchema>;
